import { faTwitch } from '@fortawesome/free-brands-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import classnames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { Modal } from 'atoms/Modal';
import { IconButton } from 'atoms/buttons/IconButton';
import { HOSTNAME } from 'config';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useTransitionApi } from 'hooks/ui/useTransitionApi';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { STORAGE, useLocalStorage } from 'hooks/useLocalStorage';

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(var(--c-rgb-blue-400), 1);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(var(--c-rgb-blue-400), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--c-rgb-blue-400), 0);
  }
`;

const Resize = styled.div`
  position: fixed;
  view-transition-name: live-stream-player;
  bottom: var(--double-unit);
  right: var(--double-unit);
  resize: both;
  overflow: auto;
  transform: rotateZ(180deg);
  width: 400px;
  height: unset !important; /** disable css resize's height override to use aspect-ratio instead */
  aspect-ratio: 16/10;
  user-select: none;
  &.shrunk {
    width: unset !important;
    aspect-ratio: 1;
    resize: none;
    overflow: visible;
  }
`;
const Player = styled.div`
  height: 100%;
  width: 100%;
  transform: rotateZ(180deg);
`;
const Close = styled.div`
  position: absolute;
  top: var(--unit);
  left: 0;
  view-transition-name: live-stream-player-button;
  .shrunk & {
    position: static;
    top: unset;
    left: unset;
    opacity: 1;
    border-radius: 50%;
    animation: ${pulse} 1.2s ease-in-out infinite;
  }
`;
const Iframe = styled.iframe`
  border: 0;
  border-radius: var(--radius-m);
  aspect-ratio: 16 / 10;
  width: 100%;
`;

export const TwitchPlayer = () => {
  const { updateDOM } = useTransitionApi();
  const to = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [fetching, setFetching] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [shrunk, setShrunk] = useLocalStorage(STORAGE.expandLiveEvent, false);
  const { currentUser } = useCurrentUserContext();
  const isLaptopAndAbove = useIsLaptop();
  const {
    flags: {
      liveStream = {
        name: 'sorareHQ',
        twitch: false,
      },
    },
  } = useFeatureFlags();

  /**
   * RequestIdleCallback but for network.
   * Make sure we display the iframe once we have loaded everything important first
   */
  useEffect(() => {
    const observer = new PerformanceObserver(() => {
      clearTimeout(to.current);
      if (fetching) {
        to.current = setTimeout(() => setFetching(false), 1000);
      }
    });
    if (isLaptopAndAbove) {
      observer.observe({ entryTypes: ['resource'] });
    }
    return () => {
      observer.disconnect();
    };
  }, [fetching, isLaptopAndAbove]);

  if (
    !liveStream.twitch ||
    !isLaptopAndAbove ||
    currentUser?.forcedIntoOnboarding
  ) {
    return null;
  }

  return (
    <Modal>
      <Resize
        className={classnames({
          shrunk,
        })}
      >
        <Player>
          {!!(loaded || shrunk) && (
            <Close>
              <IconButton
                onClick={() =>
                  updateDOM(() => {
                    setShrunk(s => !s);
                    setLoaded(false);
                  })
                }
                icon={shrunk ? faTwitch : faTimes}
                size={shrunk ? 'large' : 'small'}
                color={shrunk ? 'primary' : 'transparent'}
              />
            </Close>
          )}
          {!shrunk && !fetching && (
            <Iframe
              title="live-stream"
              src={`https://player.twitch.tv/?channel=${liveStream.name}&parent=${HOSTNAME}&muted=true&autoplay=true`}
              width="400"
              height={loaded ? '100%' : '240'}
              allowFullScreen
              onLoad={() => {
                setLoaded(true);
              }}
            />
          )}
        </Player>
      </Resize>
    </Modal>
  );
};
